import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import DataGrid, { Filter } from '#/common/components/DataGrid';
import MatchItemsModal from '#/common/components/MatchItemsModal';
import type { Asset } from '#/graphql/types';
import { gql } from '@apollo/client';
import { getAssetList } from '#/graphql/queries';
import { createMatchAssetColumns } from './columns';
import { matchStatuses } from '../../utils/constants';
import { actions } from '../AssetDataGrid/actions';
import useToast from '#/common/hooks/useToast';

const getAssetListQuery = gql(getAssetList);

export interface MatchAssetsModalProps {
  visible: boolean;
  asset?: Asset;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (selectedTisAssetId: string | null) => Promise<void> | void;
}

const MatchAssetsModal: React.FC<MatchAssetsModalProps> = ({
  visible,
  asset,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const { alert } = useToast();
  const selectedTisAssetIdRef = useRef<string | null>(null);
  const matchCandidatesGridRef = useRef<any>(null);

  const getSelectedAssetId = useCallback(() => selectedTisAssetIdRef.current, []);

  const isDefaultMatchedCandidate = useCallback(
    (candidateAsset: Asset) => {
      const isArchived =
        String(candidateAsset?.matchStatus || '').toLowerCase() ===
        matchStatuses.archived.key.toLowerCase();

      return (
        !!asset?.thirdPartyId &&
        candidateAsset?.thirdPartyId === asset.thirdPartyId &&
        isArchived &&
        candidateAsset?.id !== asset?.id
      );
    },
    [asset?.id, asset?.thirdPartyId],
  );

  const getDefaultMatchedCandidateIdFromVisibleRows = useCallback(() => {
    const visibleRows = matchCandidatesGridRef.current?.instance
      ?.getVisibleRows?.()
      ?.map((row: any) => row?.data as Asset)
      ?.filter(Boolean);

    const defaultSelectedCandidate = visibleRows?.find(isDefaultMatchedCandidate);
    return defaultSelectedCandidate?.id || null;
  }, [isDefaultMatchedCandidate]);

  const applyDefaultSelection = useCallback(() => {
    if (!visible || selectedTisAssetIdRef.current) {
      return;
    }

    const defaultMatchedCandidateId = getDefaultMatchedCandidateIdFromVisibleRows();
    if (!defaultMatchedCandidateId) {
      return;
    }

    selectedTisAssetIdRef.current = defaultMatchedCandidateId;
  }, [getDefaultMatchedCandidateIdFromVisibleRows, visible]);

  useEffect(() => {
    selectedTisAssetIdRef.current = null;
  }, [asset?.id, visible]);

  useEffect(() => {
    return () => {
      matchCandidatesGridRef.current?.instance?.off?.('contentReady', applyDefaultSelection);
    };
  }, [applyDefaultSelection]);

  const handleSelectAssetId = useCallback((id: string) => {
    selectedTisAssetIdRef.current = id;

    // Repaint rows so custom radio cells re-evaluate checked state without reloading data.
    matchCandidatesGridRef.current?.instance?.repaint?.();
  }, []);

  const handleRowClick = useCallback(
    (e: any) => {
      const rowId = e?.data?.id;
      if (rowId) {
        handleSelectAssetId(rowId);
      }
    },
    [handleSelectAssetId],
  );

  const matchCandidatesPermanentFilters = useMemo(() => {
    const matchStatusFilter = asset?.thirdPartyId
      ? [
          ['matchStatus', '=', matchStatuses.tis.key],
          'OR',
          [
            ['matchStatus', '=', matchStatuses.archived.key],
            'AND',
            ['thirdPartyId', '=', asset.thirdPartyId],
          ],
        ]
      : ['matchStatus', '=', matchStatuses.tis.key];

    const filters: Filter[] = [
      ['thirdPartyId', 'isNotNull'],
      'AND',
      ['source', 'isNotNull'],
      'AND',
      matchStatusFilter,
    ];

    if (asset?.id) filters.push('AND', ['id', '<>', asset.id]);

    if (asset?.location?.matchStatus === 'matched')
      filters.push(['AND', ['location.id', '=', asset.location.id]]);

    if (asset?.tenantId) filters.push('AND', ['tenantId', '=', asset.tenantId]);

    return filters;
  }, [asset?.id, asset?.thirdPartyId, asset?.location?.matchStatus, asset?.tenantId]);

  const matchCandidatesColumns = useMemo(
    () =>
      createMatchAssetColumns({
        getSelectedAssetId,
        onSelectAssetId: handleSelectAssetId,
        isDefaultSelectedAsset: isDefaultMatchedCandidate,
      }),
    [getSelectedAssetId, handleSelectAssetId, isDefaultMatchedCandidate],
  );

  const handleConfirm = async () => {
    let selectedId = selectedTisAssetIdRef.current;

    if (!selectedId) {
      selectedId = getDefaultMatchedCandidateIdFromVisibleRows();
    }

    if (!selectedId) {
      void alert('error', 'Please select an Asset to match');
      return;
    }

    selectedTisAssetIdRef.current = selectedId;
    await onConfirm(selectedId);
  };

  const handleClose = () => {
    selectedTisAssetIdRef.current = null;
    onClose();
  };

  const handleClearSelection = () => {
    if (!asset?.thirdPartyId) {
      void alert(
        'error',
        'The Asset is not currently matched with any TC Asset, so there is no selection to clear.',
      );
      return;
    }
    selectedTisAssetIdRef.current = null;
    void onConfirm(null);
  };

  return (
    <MatchItemsModal
      visible={visible}
      itemType="Asset"
      item={asset?.assetTag || asset?.displayNumber || asset?.id || '-'}
      isSubmitting={isSubmitting}
      warningMessage="All of the Asset's data will be updated after matching it with a new one."
      onSecondaryAction={handleClearSelection}
      onClose={handleClose}
      onConfirm={handleConfirm}>
      <DataGrid
        id="match-assets-candidates"
        pluralText="Assets"
        singularText="Asset"
        selectionMode="none"
        columns={matchCandidatesColumns}
        getItemsQuery={getAssetListQuery}
        actions={actions}
        permanentFilters={matchCandidatesPermanentFilters}
        onRowClick={handleRowClick}
        disableStateStoring
        defaultPageSize={5}
        setRef={(ref) => {
          const previousRef = matchCandidatesGridRef.current;

          previousRef?.instance?.off?.('contentReady', applyDefaultSelection);
          matchCandidatesGridRef.current = ref;

          ref?.instance?.off?.('contentReady', applyDefaultSelection);
          ref?.instance?.on?.('contentReady', applyDefaultSelection);

          applyDefaultSelection();
        }}
      />
    </MatchItemsModal>
  );
};

export default React.memo(MatchAssetsModal);
